import {NgClass} from '@angular/common';
import {Component, inject, OnInit} from '@angular/core';
import {Data, RouterOutlet} from '@angular/router';
import {DsProgressCircleModule} from '@bmw-ds/components';
import {TranslateModule} from '@ngx-translate/core';
import {ActorId, BusinessContext, IServerApi, OutletPeer} from '@service-and-repairs/awpintegrationlib';
import {AuthenticationTokenBearerService} from '../auth/authentication-token-bearer.service';
import {AnalyticsService} from '../core/analytics/services/analytics.service';
import {ServiceCaseHolder} from '../core/service-case/models/service-case-holder';
import {ServiceCaseManager} from '../core/service-case/models/service-case-manager';
import {UserInfo} from '../core/user/interfaces/user-info';
import {UserData} from '../core/user/models/user-data';
import {UserMapper} from '../core/user/models/user-mapper';
import {LanguageService} from '../core/user/services/language.service';
import {UserService} from '../core/user/services/user.service';
import {AwpClientLibRepublishService} from '../services/awp-client-lib-republish.service';
import {AwpClientLibService} from '../services/awp-client-lib.service';
import {NotificationsService} from '../services/notifications.service';
import {ThinClientService} from '../services/thin-client.service';
import {Util} from '../util/util';
import {AwpAgentComponent} from './awp-agent/awp-agent.component';
import {CustomerDataProfileComponent} from './customer-data/customer-data-profile.component';
import {HeaderComponent} from './header/header.component';
import {KoreaDisclaimerComponent} from './korea-disclaimer/korea-disclaimer.component';
import {ServiceCaseBarComponent} from './service-case-bar/case/service-case-bar.component';
import {ShortlistComponent} from './service-case-bar/case/shortlist/shortlist.component';
import {SideNavigationComponent} from './side-navigation/side-navigation.component';
import {AdvancedSearchComponent} from './vehicle/vehicle-search-overlay/advanced-search.component';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss'],
  imports: [
    HeaderComponent,
    SideNavigationComponent,
    ServiceCaseBarComponent,
    NgClass,
    DsProgressCircleModule,
    RouterOutlet,
    ShortlistComponent,
    KoreaDisclaimerComponent,
    TranslateModule,
    AwpAgentComponent,
    AdvancedSearchComponent,
    CustomerDataProfileComponent
  ]
})
export class AppComponent implements OnInit {
  private readonly thinClientService: ThinClientService = inject(ThinClientService);
  private readonly serviceCaseHolder: ServiceCaseHolder = inject(ServiceCaseHolder);
  private readonly serviceCaseManager: ServiceCaseManager = inject(ServiceCaseManager);
  private readonly languageService: LanguageService = inject(LanguageService);
  private readonly authenticationTokenBearerService: AuthenticationTokenBearerService = inject(
    AuthenticationTokenBearerService);
  private readonly userService: UserService = inject(UserService);
  private readonly analyticsService: AnalyticsService = inject(AnalyticsService);
  private readonly notificationsService: NotificationsService = inject(NotificationsService);
  private readonly awpClientLibRepublishService: AwpClientLibRepublishService = inject(AwpClientLibRepublishService);
  private readonly awpClientLibService: AwpClientLibService = inject(AwpClientLibService);
  awpLib: IServerApi;
  showCaseBar: boolean = false;
  appVisible: boolean = false;
  loading: boolean = false;
  serviceCaseActive: boolean = false;
  shortlistViewState: { visible: boolean } = {visible: false};

  // in Korea, a legal disclaimer has to be displayed on every login
  koreaDisclaimerState = {visible: false};

  constructor() {
    // Subscribe to republish requests from AWP client lib
    this.awpClientLibRepublishService.init();
    this.awpLib = this.awpClientLibService.serverApi;

    this.subscribeToNotifications();

    this.serviceCaseManager.caseLoadingStarted.subscribe((): void => {
      this.loading = true;
    });
    this.serviceCaseManager.caseLoadingFinished.subscribe((): void => {
      this.loading = false;
    });
    this.serviceCaseHolder.activeServiceCaseChanged.subscribe((): void => {
      this.serviceCaseActive = !!this.serviceCaseHolder.getActiveCase();
      this.shortlistViewState.visible = this.shortlistViewState.visible && this.serviceCaseActive;
    });
  }

  ngOnInit(): void {
    this.registerBrowserErrorTelemetry();

    // User is preloaded by OidcFlow.js, show app once request has finished
    this.waitForUserToLoad()
      .then((userInfo: UserInfo): void => this.onUserLoaded(UserMapper.fromUserInfo(userInfo)))
      .catch((e: unknown): void => console.error(Util.logPrefix('Webapp', 'App')
        + 'Error while waiting for user to load.', e));
  }

  onRouteActivate(activatedRouteData: Data): void {
    // Run asynchronously to prevent updates between Angular change detection runs
    setTimeout((): void => {
      this.showCaseBar = !activatedRouteData.appConfiguration.hideCaseBar;
    }, 0);
  }

  onRouteDeactivate(): void {
    this.showCaseBar = false;
  }

  private waitForUserToLoad(): Promise<UserInfo> {
    return new Promise<UserInfo>(resolve => {
      // Return user data, if already available
      if (window['bmwNamespace']?.userData) {
        resolve(window['bmwNamespace'].userData);
      }
      // Subscribe for user data to become available, return once available
      else {
        window.addEventListener('userDataLoaded', (): void => resolve(window['bmwNamespace'].userData));
      }
    });
  }

  private registerBrowserErrorTelemetry(): void {
    window.addEventListener('error', (event: ErrorEvent): void => {
      const errorName = event.error instanceof Error ? event.error.name : 'UnknownError';
      const errorMessage = event.error instanceof Error ? event.error.message : event.message;
      console.error(Util.logPrefix('Webapp', 'Browser error')
        + `${errorName}: ${errorMessage}. Source: ${event.filename}:${event.lineno}:${event.colno}.`);
    });
    window.addEventListener('unhandledrejection', (event: PromiseRejectionEvent): void => {
      console.error(Util.logPrefix('Webapp', 'Browser rejection')
        + `Unhandled promise rejection: ${this.getRejectionDescription(event.reason)}.`);
    });
  }

  private getRejectionDescription(reason: unknown): string {
    if (reason instanceof Error) {
      return `${reason.name}: ${reason.message}`;
    }
    if (typeof reason === 'string') {
      return reason;
    }
    return Object.prototype.toString.call(reason);
  }

  private onUserLoaded(userData: UserData): void {
    // Section 1: Guard bootstrap for invalid B2D business relationships
    // Don't show webapp if user is in B2D but has no business relationships
    if (userData.getContext() === BusinessContext.B2D && !userData.hasValidBusinessRelationship()) {
      return;
    }

    // Section 2: Start independent startup tasks in parallel
    const allOutletPeersPromise: Promise<void> = this.userService.getOutletPeers('ALL')
      .then((peers: OutletPeer[]): void => userData.setAllOutletPeers(peers));

    const initialCookieSyncPromise: Promise<void> = this.authenticationTokenBearerService.ensureInitialAwpCookieSync()
      .catch((): void => console.warn(Util.logPrefix('Webapp', 'App')
        + 'Initial awp_cookie synchronization failed. Continuing app bootstrap.'));

    const csslTokenPromise: Promise<void> = this.authenticationTokenBearerService.fetchAndStoreCsslToken(userData)
      .then((): void => console.info(Util.logPrefix('Webapp', 'App')
        + 'User data and CSSL token available. Loading app.'))
      .catch((): void => console.warn(Util.logPrefix('Webapp', 'App')
        + 'User data available but CSSL token is missing. Loading app.'));

    // Section 3: Finalize bootstrap after all startup tasks settled
    Promise.allSettled([allOutletPeersPromise, initialCookieSyncPromise, csslTokenPromise])
      .finally((): void => {
        this.userService.publishUser(userData);
        this.showDisclaimerForKoreanUsers();
        this.analyticsService.postUserLoggedInEvent();
        this.languageService.use(userData.getLocale()).then(this.setAppVisible.bind(this));
      });
  }

  private showDisclaimerForKoreanUsers(): void {
    this.koreaDisclaimerState.visible = this.userService.getCurrentUser()?.getB2XCountryCode() === 'KR';
  }

  private setAppVisible(): void {
    this.appVisible = true;
    document.getElementById('splash-screen')?.remove();
    this.thinClientService.initialize();
  }

  private subscribeToNotifications(): void {
    this.awpLib.subscribeToInfoNotifications((msg: string, origin?: ActorId): void => this.notificationsService.showInfoNotification(msg, undefined, origin));
    this.awpLib.subscribeToSuccessNotifications((msg: string, origin?: ActorId): void => this.notificationsService.showSuccessNotification(msg, origin));
    this.awpLib.subscribeToWarningNotifications((msg: string, origin?: ActorId): void => this.notificationsService.showWarningNotification(msg, origin));
    this.awpLib.subscribeToErrorNotifications((msg: string, origin?: ActorId): void => this.notificationsService.showErrorNotification(msg, origin));
  }
}
